import {
  AloftArea,
  AloftPrice,
  CraftsmanCostProps,
  DEFAULT_CORNERS,
  getCraftsmanCost,
  isDefined,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectCompSequence, selectEffectiveDate } from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectCostProps } from './selectCostProps.ts';

const calculateADUCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  effectiveDate: ReturnType<typeof selectEffectiveDate>,
  id: string,
) => {
  const improvementsForTransaction = improvements[id];
  const propertyIdentificationForTransaction = propertyIdentification[id];

  if (
    isUndefined(improvementsForTransaction) ||
    isUndefined(propertyIdentificationForTransaction)
  ) {
    return undefined;
  }

  // for now we are just using corners from the main dwelling, which is
  // currently just always 6. also, bathrooms are included in the sfr
  // cost, and we are currently only collecting the inputs for reference.
  const { adu, corners } = improvementsForTransaction;
  const {
    address: { PostalCode: zipCode },
  } = propertyIdentificationForTransaction;

  if (
    isUndefined(zipCode) ||
    isUndefined(adu) ||
    isUndefined(adu.area) ||
    isUndefined(adu.quality) ||
    isUndefined(effectiveDate)
  ) {
    return undefined;
  }

  const calculator = getCraftsmanCost(costProps);

  return calculator.getADUCost({
    quality: adu.quality,
    sqft: AloftArea.schema.parse(adu.area),
    fullBaths: adu.fullBaths,
    halfBaths: adu.halfBaths,
    zipCode,
    corners: corners ?? DEFAULT_CORNERS,
  });
};

const getTotalOrBreakdownADUCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  effectiveDate: ReturnType<typeof selectEffectiveDate>,
  id: string,
  type: 'totalCost' | 'zipOnly',
) => {
  const costOutput = calculateADUCost(
    costProps,
    improvements,
    propertyIdentification,
    effectiveDate,
    id,
  );

  if (costOutput === undefined) {
    return AloftPrice.zero;
  }

  return type === 'totalCost' ? costOutput.totalCost : costOutput.multiplierBreakdown[type];
};

const getTotalADUCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  effectiveDate: ReturnType<typeof selectEffectiveDate>,
  id: string,
) =>
  getTotalOrBreakdownADUCost(
    costProps,
    improvements,
    propertyIdentification,
    effectiveDate,
    id,
    'totalCost',
  );

export const selectADUCost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    selectEffectiveDate,
    (state, id: string) => id,
  ],
  getTotalADUCost,
);

export const selectZipOnlyADUCost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    selectEffectiveDate,
    (state, id: string) => id,
  ],
  (costProps, improvements, propertyIdentification, effectiveDate, id) =>
    getTotalOrBreakdownADUCost(
      costProps,
      improvements,
      propertyIdentification,
      effectiveDate,
      id,
      'zipOnly',
    ),
);

export const selectADUCosts = createSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    selectEffectiveDate,
    selectCompSequence,
  ],
  (costProps, improvements, propertyIdentificationEntities, effectiveDate, compSequence) => {
    const properties = ['subject', ...compSequence];

    return properties.reduce((acc: Record<string, AloftPrice>, propID) => {
      const cost = getTotalADUCost(
        costProps,
        improvements,
        propertyIdentificationEntities,
        effectiveDate,
        propID,
      );
      if (isDefined(cost)) acc[propID] = cost;
      return acc;
    }, {});
  },
);

export const selectADUCostDisplay = createSelector([selectADUCost], (ADUCost) => {
  if (isUndefined(ADUCost)) {
    return undefined;
  }

  return ADUCost.toString(true);
});
